import { createContext, useContext, useState, useCallback, useMemo, type ReactNode } from "react";
import {
  useKeyboardShortcuts,
  formatShortcut,
  type ShortcutDefinition,
} from "../hooks/useKeyboardShortcuts";

type AppMode = "stream" | "outline" | "edit";

interface KeyboardShortcutsContextType {
  activeMode: AppMode;
  setActiveMode: (mode: AppMode) => void;
  isHelpModalOpen: boolean;
  openHelpModal: () => void;
  closeHelpModal: () => void;
  toggleHelpModal: () => void;
  shortcuts: ShortcutDefinition[];
  registerShortcut: (shortcut: ShortcutDefinition) => () => void;
  unregisterShortcut: (key: string, modifiers?: string[]) => void;
}

const KeyboardShortcutsContext = createContext<KeyboardShortcutsContextType | null>(null);

interface KeyboardShortcutsProviderProps {
  children: ReactNode;
  onSave?: () => void;
  onExport?: () => void;
  onToggleFullscreen?: () => void;
  onModeChange?: (mode: AppMode) => void;
  onToggleFocusMode?: () => void;
  onAcceptSuggestion?: () => void;
  onDismissSuggestion?: () => void;
  onAcceptAllSuggestions?: () => void;
  onRejectAllSuggestions?: () => void;
  onAddOutlineItem?: () => void;
  onIndentOutlineItem?: () => void;
  onOutdentOutlineItem?: () => void;
}

function createShortcutKey(key: string, modifiers?: string[]): string {
  const mods = modifiers ? [...modifiers].sort().join("+") : "";
  return `${mods}:${key}`;
}

export function KeyboardShortcutsProvider({
  children,
  onSave,
  onExport,
  onToggleFullscreen,
  onModeChange,
  onToggleFocusMode,
  onAcceptSuggestion,
  onDismissSuggestion,
  onAcceptAllSuggestions,
  onRejectAllSuggestions,
  onAddOutlineItem,
  onIndentOutlineItem,
  onOutdentOutlineItem,
}: KeyboardShortcutsProviderProps) {
  const [activeMode, setActiveModeState] = useState<AppMode>("stream");
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  const [dynamicShortcuts, setDynamicShortcuts] = useState<ShortcutDefinition[]>([]);

  const openHelpModal = useCallback(() => setIsHelpModalOpen(true), []);
  const closeHelpModal = useCallback(() => setIsHelpModalOpen(false), []);
  const toggleHelpModal = useCallback(() => setIsHelpModalOpen((prev) => !prev), []);

  const setActiveMode = useCallback(
    (mode: AppMode) => {
      setActiveModeState(mode);
      onModeChange?.(mode);
    },
    [onModeChange],
  );

  const globalShortcuts: ShortcutDefinition[] = useMemo(
    () => [
      {
        key: "s",
        modifiers: ["meta"],
        action: () => onSave?.(),
        description: "Save document",
        category: "global",
        enabled: !!onSave,
      },
      {
        key: "e",
        modifiers: ["meta", "shift"],
        action: () => onExport?.(),
        description: "Export document",
        category: "global",
        enabled: !!onExport,
      },
      {
        key: "Enter",
        modifiers: ["meta"],
        action: () => onToggleFullscreen?.(),
        description: "Toggle fullscreen",
        category: "global",
        enabled: !!onToggleFullscreen,
      },
      {
        key: "1",
        modifiers: ["meta"],
        action: () => setActiveMode("stream"),
        description: "Switch to Stream mode",
        category: "global",
      },
      {
        key: "2",
        modifiers: ["meta"],
        action: () => setActiveMode("outline"),
        description: "Switch to Outline mode",
        category: "global",
      },
      {
        key: "3",
        modifiers: ["meta"],
        action: () => setActiveMode("edit"),
        description: "Switch to Edit mode",
        category: "global",
      },
      {
        key: "?",
        modifiers: ["meta"],
        action: toggleHelpModal,
        description: "Show keyboard shortcuts",
        category: "global",
      },
      {
        key: "Escape",
        action: closeHelpModal,
        description: "Close modal / Dismiss",
        category: "global",
        enabled: isHelpModalOpen,
      },
    ],
    [
      onSave,
      onExport,
      onToggleFullscreen,
      setActiveMode,
      toggleHelpModal,
      closeHelpModal,
      isHelpModalOpen,
    ],
  );

  const editModeShortcuts: ShortcutDefinition[] = useMemo(
    () => [
      {
        key: "f",
        modifiers: ["meta"],
        action: () => onToggleFocusMode?.(),
        description: "Toggle focus mode",
        category: "edit",
        enabled: activeMode === "edit" && !!onToggleFocusMode,
      },
      {
        key: "Tab",
        action: () => onAcceptSuggestion?.(),
        description: "Accept highlighted suggestion",
        category: "edit",
        enabled: activeMode === "edit" && !!onAcceptSuggestion,
      },
      {
        key: "Escape",
        action: () => onDismissSuggestion?.(),
        description: "Dismiss suggestion",
        category: "edit",
        enabled: activeMode === "edit" && !!onDismissSuggestion && !isHelpModalOpen,
      },
      {
        key: "a",
        modifiers: ["meta"],
        action: () => onAcceptAllSuggestions?.(),
        description: "Accept all suggestions",
        category: "edit",
        enabled: activeMode === "edit" && !!onAcceptAllSuggestions,
      },
      {
        key: "a",
        modifiers: ["meta", "shift"],
        action: () => onRejectAllSuggestions?.(),
        description: "Reject all suggestions",
        category: "edit",
        enabled: activeMode === "edit" && !!onRejectAllSuggestions,
      },
    ],
    [
      activeMode,
      isHelpModalOpen,
      onToggleFocusMode,
      onAcceptSuggestion,
      onDismissSuggestion,
      onAcceptAllSuggestions,
      onRejectAllSuggestions,
    ],
  );

  const outlineModeShortcuts: ShortcutDefinition[] = useMemo(
    () => [
      {
        key: "Enter",
        action: () => onAddOutlineItem?.(),
        description: "Add new item",
        category: "outline",
        enabled: activeMode === "outline" && !!onAddOutlineItem,
      },
      {
        key: "Tab",
        action: () => onIndentOutlineItem?.(),
        description: "Indent item",
        category: "outline",
        enabled: activeMode === "outline" && !!onIndentOutlineItem,
      },
      {
        key: "Tab",
        modifiers: ["shift"],
        action: () => onOutdentOutlineItem?.(),
        description: "Outdent item",
        category: "outline",
        enabled: activeMode === "outline" && !!onOutdentOutlineItem,
      },
      {
        key: "ArrowUp",
        modifiers: ["alt"],
        action: () => {},
        description: "Move item up",
        category: "outline",
        enabled: activeMode === "outline",
      },
      {
        key: "ArrowDown",
        modifiers: ["alt"],
        action: () => {},
        description: "Move item down",
        category: "outline",
        enabled: activeMode === "outline",
      },
    ],
    [activeMode, onAddOutlineItem, onIndentOutlineItem, onOutdentOutlineItem],
  );

  const allShortcuts = useMemo(
    () => [...globalShortcuts, ...editModeShortcuts, ...outlineModeShortcuts, ...dynamicShortcuts],
    [globalShortcuts, editModeShortcuts, outlineModeShortcuts, dynamicShortcuts],
  );

  const registerShortcut = useCallback((shortcut: ShortcutDefinition) => {
    setDynamicShortcuts((prev) => [...prev, shortcut]);
    return () => {
      setDynamicShortcuts((prev) =>
        prev.filter(
          (s) =>
            createShortcutKey(s.key, s.modifiers) !==
            createShortcutKey(shortcut.key, shortcut.modifiers),
        ),
      );
    };
  }, []);

  const unregisterShortcut = useCallback((key: string, modifiers?: string[]) => {
    setDynamicShortcuts((prev) =>
      prev.filter(
        (s) => createShortcutKey(s.key, s.modifiers) !== createShortcutKey(key, modifiers),
      ),
    );
  }, []);

  useKeyboardShortcuts({
    shortcuts: allShortcuts,
    enabled: true,
    ignoreInputElements: true,
  });

  const displayableShortcuts = useMemo(() => {
    const seen = new Set<string>();
    return allShortcuts.filter((s) => {
      const key = `${s.category}:${s.description}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [allShortcuts]);

  const value = useMemo<KeyboardShortcutsContextType>(
    () => ({
      activeMode,
      setActiveMode,
      isHelpModalOpen,
      openHelpModal,
      closeHelpModal,
      toggleHelpModal,
      shortcuts: displayableShortcuts,
      registerShortcut,
      unregisterShortcut,
    }),
    [
      activeMode,
      setActiveMode,
      isHelpModalOpen,
      openHelpModal,
      closeHelpModal,
      toggleHelpModal,
      displayableShortcuts,
      registerShortcut,
      unregisterShortcut,
    ],
  );

  return (
    <KeyboardShortcutsContext.Provider value={value}>{children}</KeyboardShortcutsContext.Provider>
  );
}

export function useKeyboardShortcutsContext(): KeyboardShortcutsContextType {
  const context = useContext(KeyboardShortcutsContext);
  if (!context) {
    throw new Error("useKeyboardShortcutsContext must be used within a KeyboardShortcutsProvider");
  }
  return context;
}

export { formatShortcut };
export type { ShortcutDefinition };
